/**
 * usePreloadStashes - Pre-populate React Query cache with post data
 *
 * Use this when you already have post data (e.g., list results)
 * and want to avoid redundant fetches on stash detail views.
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { FeedPost } from "@/hooks/useRecentStashes";
import { stashListQueryKey } from "@/features/stash/stashQueryKeys";

type PreloadPost = FeedPost & { stasht_id?: string };

const resolvePostId = (post: PreloadPost): string | undefined => {
  return post.id || post.messageId || post.stasht_id;
};

/**
 * Pre-loads posts into React Query cache.
 * Stash detail queries reuse this list-level stash cache entry.
 */
export function usePreloadStashes(posts: PreloadPost[] | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!posts || posts.length === 0) return;

    posts.forEach(post => {
      if (!post) return;
      const id = resolvePostId(post);
      if (!id) return;

      const existing = queryClient.getQueryData(stashListQueryKey(id));
      if (!existing) {
        queryClient.setQueryData(stashListQueryKey(id), post);
      }
    });
  }, [posts, queryClient]);
}

/**
 * Pre-loads a single post into React Query cache.
 */
export function usePreloadPost(post: PreloadPost | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!post) return;
    const id = resolvePostId(post);
    if (!id) return;

    const existing = queryClient.getQueryData(stashListQueryKey(id));
    if (!existing) {
      queryClient.setQueryData(stashListQueryKey(id), post);
    }
  }, [post, queryClient]);
}
